<template>
  <div class="pt-1">
    <div class="d-flex justify-content-between align-items-center mb-1">
      <small class="text-muted">Filled in with this application's values. Send the PDF to the partner's developers.</small>
      <b-button size="sm" variant="primary" @click="exportPdf">Export PDF</b-button>
    </div>

    <div ref="guide" class="signin-guide">
      <h3>{{ application.title }}: signing in with PAC</h3>
      <p class="muted">Integration guide, generated {{ today }}</p>

      <h4>1. Your values</h4>
      <table class="table table-sm table-bordered">
        <tbody>
          <tr><th>Application ID (<code>client_id</code>)</th><td><code>{{ application._id }}</code></td></tr>
          <tr><th>PAC server</th><td><code>{{ root }}</code></td></tr>
          <tr><th>OIDC discovery</th><td><code>{{ root }}/.well-known/openid-configuration</code></td></tr>
          <tr><th>Registered redirect URIs</th><td><code v-for="uri in redirectUris" :key="uri" class="d-block">{{ uri }}</code><span v-if="!redirectUris.length">None yet. Ask the PAC administrator to add yours.</span></td></tr>
          <tr><th>Redirect sign-in lands on</th><td><code>{{ redirectLanding }}</code></td></tr>
          <tr><th>Access</th><td>{{ accessText }}</td></tr>
        </tbody>
      </table>
      <p>
        The Application ID is public and safe to put in URLs. It is not an API client ID (those start with
        <code>pac_live_</code> or <code>pac_test_</code>) and it is not the application access token.
      </p>

      <h4>2. Choose a sign-in method</h4>
      <table class="table table-sm table-bordered">
        <thead><tr><th>Method</th><th>Use it for</th><th>What you receive</th></tr></thead>
        <tbody>
          <tr><td>Redirect sign-in</td><td>Web apps the person opens from their PAC dashboard</td><td>A PAC session token (24 hours, no refresh) that you check with the application access token</td></tr>
          <tr><td>OIDC with PKCE</td><td>Web, mobile and desktop apps that start sign-in themselves</td><td>Standard OIDC tokens: access token (15 min), ID token, refresh token (30 days)</td></tr>
        </tbody>
      </table>

      <h4>3. Redirect sign-in</h4>
      <p><b>You need:</b> the application access token from PAC (Integration, Credentials tab), kept on your server only.</p>
      <ol>
        <li>The person opens <b>{{ application.title }}</b> from their PAC dashboard. PAC checks access{{ application.require_otp ? " and asks for a one-time code" : "" }}.</li>
        <li>PAC sends the browser to <code>{{ redirectLanding }}?access_token=…</code></li>
        <li>Read <code>access_token</code> from the query string, then remove it from the address bar straight away. Never log the URL, and send <code>Referrer-Policy: no-referrer</code>.</li>
        <li>From your server, check the token with introspect. Start your own session only when <code>active</code> is <code>true</code> and <code>app_access</code> is <code>true</code>.</li>
        <li>Call introspect again every 5 minutes, and call logout when the person signs out.</li>
      </ol>
      <pre>{{ snippets.introspect }}</pre>
      <table class="table table-sm table-bordered">
        <thead><tr><th>Call</th><th>Body</th><th>Returns</th></tr></thead>
        <tbody>
          <tr><td><code>POST {{ api }}/pac/introspect</code></td><td><code>{ "token" }</code></td><td>Always 200. <code>{ active: false }</code>, or <code>active: true</code> with <code>sub</code>, <code>status</code>, <code>profile_id</code>, <code>minister_id</code>, <code>staff_id</code>, <code>member_type_id</code>, <code>org_unit</code>, <code>department_id</code>, <code>app_access</code></td></tr>
          <tr><td><code>POST {{ api }}/pac/logout</code></td><td><code>{ "token" }</code></td><td>Ends the PAC session</td></tr>
          <tr><td><code>GET {{ api }}/pac/whoami</code></td><td>none</td><td>Your registered application. Use it once to test the access token</td></tr>
        </tbody>
      </table>
      <p>Every call sends the header <code>x-pac-access-token: &lt;application access token&gt;</code>.</p>

      <h4>4. OIDC with PKCE</h4>
      <p>
        No client secret is used. Most OIDC libraries do steps 1 to 3 for you: point them at the discovery URL and set
        the Application ID, your redirect URI and the scope.
      </p>

      <h5>Step 1: send the browser to authorize</h5>
      <pre>{{ snippets.authorize }}</pre>
      <table class="table table-sm table-bordered">
        <thead><tr><th>Parameter</th><th>Required</th><th>Value and where it comes from</th></tr></thead>
        <tbody>
          <tr><td><code>client_id</code></td><td>Yes</td><td><code>{{ application._id }}</code>, the Application ID above</td></tr>
          <tr><td><code>redirect_uri</code></td><td>Yes</td><td>One of the registered redirect URIs, exactly as registered, URL-encoded</td></tr>
          <tr><td><code>response_type</code></td><td>Yes</td><td><code>code</code></td></tr>
          <tr><td><code>code_challenge</code></td><td>Yes</td><td>Made by your app for each sign-in: <code>BASE64URL(SHA256(code_verifier))</code></td></tr>
          <tr><td><code>code_challenge_method</code></td><td>Yes</td><td><code>S256</code> (<code>plain</code> is refused)</td></tr>
          <tr><td><code>scope</code></td><td>No</td><td>Space-separated, default <code>openid</code>. Use <code>openid profile</code>, and add <code>openid</code> to receive an ID token</td></tr>
          <tr><td><code>state</code></td><td>Recommended</td><td>A random value your app makes and stores. Compare it on return</td></tr>
          <tr><td><code>nonce</code></td><td>Recommended</td><td>A random value your app makes and stores. It comes back inside the ID token</td></tr>
        </tbody>
      </table>
      <pre>{{ snippets.pkce }}</pre>

      <h5>Step 2: PAC returns to your redirect URI</h5>
      <pre>{{ snippets.callback }}</pre>
      <p>
        Check that <code>state</code> matches the stored value. The <code>code</code> works once and expires after
        <b>60 seconds</b>, so exchange it immediately. <code>?error=access_denied</code> means the person has not been given
        access to this application.
      </p>

      <h5>Step 3: exchange the code for tokens</h5>
      <pre>{{ snippets.token }}</pre>
      <table class="table table-sm table-bordered">
        <thead><tr><th>Field</th><th>Required</th><th>Value</th></tr></thead>
        <tbody>
          <tr><td><code>grant_type</code></td><td>Yes</td><td><code>authorization_code</code></td></tr>
          <tr><td><code>client_id</code></td><td>Yes</td><td>The same Application ID</td></tr>
          <tr><td><code>code</code></td><td>Yes</td><td>From the callback</td></tr>
          <tr><td><code>redirect_uri</code></td><td>Yes</td><td>Exactly the value sent in step 1</td></tr>
          <tr><td><code>code_verifier</code></td><td>Yes</td><td>The original verifier, not the challenge</td></tr>
        </tbody>
      </table>
      <pre>{{ snippets.tokenResponse }}</pre>

      <h5>Step 4: use and renew the tokens</h5>
      <table class="table table-sm table-bordered">
        <thead><tr><th>Call</th><th>Send</th><th>Notes</th></tr></thead>
        <tbody>
          <tr><td><code>GET {{ root }}/oauth/userinfo</code></td><td><code>Authorization: Bearer &lt;access_token&gt;</code></td><td>Returns <code>sub</code>, <code>email</code>, <code>profile_id</code>, <code>minister_id</code>, <code>staff_id</code>. 401 when the person is deactivated</td></tr>
          <tr><td><code>POST {{ root }}/oauth/token</code></td><td><code>grant_type=refresh_token</code>, <code>client_id</code>, <code>refresh_token</code></td><td>Each refresh token works once. Store the new one every time. Reusing an old one signs the person out everywhere</td></tr>
          <tr><td><code>POST {{ root }}/oauth/revoke</code></td><td><code>token=&lt;refresh_token&gt;</code></td><td>Signs the device out. Always 200</td></tr>
          <tr><td><code>GET {{ root }}/.well-known/jwks.json</code></td><td>none</td><td>RS256 public keys for checking tokens. <code>aud</code> is the Application ID. Fetch again on an unknown <code>kid</code></td></tr>
        </tbody>
      </table>
      <p>Use <code>profile_id</code> as the person's identifier. Some people have no PAC login, so do not key records on the user ID.</p>

      <h4>5. Errors</h4>
      <table class="table table-sm table-bordered">
        <thead><tr><th>Error</th><th>Cause</th><th>Fix</th></tr></thead>
        <tbody>
          <tr><td><code>Unknown client</code></td><td><code>client_id</code> is not this application's ID, or the application is inactive</td><td>Use <code>{{ application._id }}</code></td></tr>
          <tr><td><code>redirect_uri is not registered</code></td><td>The URI differs from every registered one (slash, port, http or https)</td><td>Ask the PAC administrator to register it exactly</td></tr>
          <tr><td><code>unsupported_response_type</code></td><td><code>response_type</code> is not <code>code</code></td><td>Send <code>code</code></td></tr>
          <tr><td><code>invalid_request</code></td><td>A required value is missing, or the method is not S256</td><td>Check the tables above</td></tr>
          <tr><td><code>invalid_client</code></td><td>Wrong <code>client_id</code> at the token endpoint</td><td>Send the same Application ID as step 1</td></tr>
          <tr><td><code>invalid_grant</code></td><td>Code expired or reused, verifier or redirect URI mismatch, refresh token reused or expired</td><td>Start sign-in again</td></tr>
          <tr><td><code>access_denied</code></td><td>The person has not been given access to this application</td><td>Ask the PAC administrator to grant access</td></tr>
          <tr><td><code>temporarily_unavailable</code> (503)</td><td>PAC's signing key is not configured</td><td>Contact the PAC team</td></tr>
        </tbody>
      </table>

      <h4>6. Before going live</h4>
      <ul>
        <li>Register the production redirect URI and remove <code>localhost</code> entries.</li>
        <li>Keep the application access token on your server. It never goes to a browser or mobile app.</li>
        <li>Check <code>state</code> and <code>nonce</code> on every sign-in.</li>
        <li>Store refresh tokens encrypted and replace them on every refresh.</li>
        <li>Sign in with a test user who has access, and with one who does not, and confirm both outcomes.</li>
      </ul>
    </div>
  </div>
</template>

<script>
import { BButton } from "bootstrap-vue";

// Everything printed to the PDF lives inside .signin-guide; these styles go with it into the print window
const PRINT_STYLES = `
  body { font: 12px/1.5 -apple-system, "Segoe UI", Roboto, Arial, sans-serif; color: #1f2430; margin: 24px; }
  h3 { font-size: 20px; margin: 0 0 4px; } h4 { font-size: 15px; margin: 22px 0 8px; border-bottom: 1px solid #d8dbe3; padding-bottom: 4px; }
  h5 { font-size: 13px; margin: 16px 0 6px; } .muted { color: #6b7180; margin-top: 0; }
  table { width: 100%; border-collapse: collapse; margin: 8px 0 12px; page-break-inside: avoid; }
  th, td { border: 1px solid #d8dbe3; padding: 5px 7px; text-align: left; vertical-align: top; }
  th { background: #f3f4f7; } code { font: 11px Menlo, Consolas, monospace; word-break: break-all; }
  .d-block { display: block; }
  pre { font: 11px/1.45 Menlo, Consolas, monospace; background: #f3f4f7; border: 1px solid #d8dbe3; padding: 8px; white-space: pre-wrap; word-break: break-all; page-break-inside: avoid; }
`;

export default {
  components: { BButton },
  props: {
    application: { type: Object, required: true },
  },
  computed: {
    // OAuth endpoints live at the API root, the /pac endpoints under /api/v1
    api() {
      return (process.env.VUE_APP_SERVER_URL || "").replace(/\/$/, "");
    },
    root() {
      return this.api.replace(/\/api\/v1$/, "");
    },
    redirectUris() {
      return this.application.redirect_uris || [];
    },
    redirectLanding() {
      return `${(this.application.full_url || "https://your-app.example").replace(/\/$/, "")}/auth/pac`;
    },
    accessText() {
      const who = this.application.access_type === "private" ? "Only people PAC has given access to this application" : "Any active PAC user";
      return this.application.require_otp ? `${who}. A one-time code is required on redirect sign-in.` : `${who}.`;
    },
    today() {
      return new Date().toLocaleDateString();
    },
    snippets() {
      const id = this.application._id;
      const redirect = this.redirectUris[0] || "https://your-app.example/auth/callback";
      return {
        introspect: [
          `curl -X POST ${this.api}/pac/introspect \\`,
          `  -H "x-pac-access-token: <application access token>" \\`,
          `  -H "Content-Type: application/json" \\`,
          `  -d '{ "token": "<access_token from the URL>" }'`,
        ].join("\n"),
        authorize: [
          `${this.root}/oauth/authorize`,
          `  ?client_id=${id}`,
          `  &redirect_uri=${encodeURIComponent(redirect)}`,
          "  &response_type=code",
          "  &scope=openid%20profile",
          "  &state=<random>",
          "  &nonce=<random>",
          "  &code_challenge=<from code_verifier>",
          "  &code_challenge_method=S256",
        ].join("\n"),
        pkce: [
          "// Node.js: make a new pair for every sign-in and keep the verifier for step 3",
          'const code_verifier  = crypto.randomBytes(32).toString("base64url");',
          'const code_challenge = crypto.createHash("sha256").update(code_verifier).digest("base64url");',
          'const state = crypto.randomBytes(24).toString("base64url");',
          'const nonce = crypto.randomBytes(24).toString("base64url");',
        ].join("\n"),
        callback: `${redirect}?code=<one-time code>&state=<your state>`,
        token: [
          `curl -X POST ${this.root}/oauth/token \\`,
          "  -d grant_type=authorization_code \\",
          `  -d client_id=${id} \\`,
          "  -d code=<code from the callback> \\",
          `  -d redirect_uri=${redirect} \\`,
          "  -d code_verifier=<verifier from step 1>",
        ].join("\n"),
        tokenResponse: JSON.stringify({
          token_type: "Bearer",
          access_token: "<RS256 JWT, 15 minutes>",
          expires_in: 900,
          refresh_token: "<30 days, single use>",
          scope: "openid profile",
          id_token: "<RS256 JWT, when scope has openid>",
        }, null, 2),
      };
    },
  },
  methods: {
    // Prints the guide alone in a new window; the browser's print dialog offers "Save as PDF"
    exportPdf() {
      const win = window.open("", "_blank");
      if (!win) {
        this.$emit("blocked");
        return;
      }
      const title = String(this.application.title || "").replace(/[<&]/g, (c) => (c === "<" ? "&lt;" : "&amp;"));
      win.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>${title} - PAC sign-in guide</title><style>${PRINT_STYLES}</style></head><body>${this.$refs.guide.innerHTML}</body></html>`);
      win.document.close();
      win.focus();
      setTimeout(() => win.print(), 300);
    },
  },
};
</script>

<style scoped>
.signin-guide h4 { margin-top: 1.5rem; padding-bottom: 0.25rem; border-bottom: 1px solid rgba(130, 134, 139, 0.3); }
.signin-guide h5 { margin-top: 1rem; }
.signin-guide pre { white-space: pre-wrap; word-break: break-all; padding: 0.75rem; border-radius: 0.357rem; background: rgba(130, 134, 139, 0.12); }
.signin-guide code { word-break: break-all; }
.signin-guide .muted { opacity: 0.7; }
</style>
